import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ExternalLink,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Play,
  Image,
} from "lucide-react";
import { projectDetails } from "../../data/Projects";

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const projectId = parseInt(id || "1");
  const project =
    projectDetails.find((p) => p.id === projectId) || projectDetails[0];
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

  const nextMedia = () => {
    setCurrentMediaIndex((prev) => (prev + 1) % project.media.length);
  };

  const prevMedia = () => {
    setCurrentMediaIndex(
      (prev) => (prev - 1 + project.media.length) % project.media.length
    );
  };

  const getMediaType = (url: string) => {
    if (
      url.includes("youtube.com") ||
      url.includes("youtu.be") ||
      url.includes("vimeo")
    ) {
      return "video";
    }
    if (url.includes("pinterest") || url.includes("pin.it")) {
      return "pinterest";
    }
    if (url.match(/\.(jpeg|jpg|gif|png|webp)$/)) {
      return "image";
    }
    if (url.match(/\.(mp4|webm|ogg)$/)) {
      return "video";
    }
    return "image";
  };

  const extractPinterestId = (url: string) => {
    // Untuk embed URL seperti: https://assets.pinterest.com/ext/embed.html?id=1013661828639613273
    const match = url.match(/id=(\d+)/);
    if (match) return match[1];

    // Untuk Pinterest pin URL: https://www.pinterest.com/pin/1013661828639613273/
    const pinMatch = url.match(/pin\/(\d+)/);
    if (pinMatch) return pinMatch[1];

    return null;
  };

  const renderMedia = (media: {
    url: string;
    type?: string;
    thumbnail?: string;
  }) => {
    const mediaType = media.type || getMediaType(media.url);

    if (mediaType === "video") {
      // YouTube embed
      if (media.url.includes("youtube.com") || media.url.includes("youtu.be")) {
        const videoId = media.url.match(
          /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
        )?.[1];
        return (
          <div className="relative w-full h-0 pb-[56.25%]">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}`}
              className="absolute top-0 left-0 w-full h-full rounded-lg"
              allowFullScreen
              title="YouTube video"
            />
          </div>
        );
      }

      // Vimeo embed
      if (media.url.includes("vimeo")) {
        const videoId = media.url.match(/vimeo\.com\/(\d+)/)?.[1];
        return (
          <div className="relative w-full h-0 pb-[56.25%]">
            <iframe
              src={`https://player.vimeo.com/video/${videoId}`}
              className="absolute top-0 left-0 w-full h-full rounded-lg"
              allowFullScreen
              title="Vimeo video"
            />
          </div>
        );
      }

      // Generic video
      return (
        <div className="flex justify-center">
          <video
            className="w-auto max-h-[75vh] rounded-lg"
            controls
            poster={media.thumbnail}
          >
            <source src={media.url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    if (mediaType === "pinterest") {
      return (
        <div className="flex justify-center">
          <div className="w-full max-w-md">
            {" "}
            {/* Limit width for Pinterest */}
            <iframe
              src={media.url}
              height="600"
              width="100%"
              frameBorder="0"
              scrolling="no"
              className="rounded-lg"
              title="Pinterest Pin"
            />
          </div>
        </div>
      );

      // Fallback untuk Pinterest URL yang tidak bisa di-embed
      return (
        <div className="flex flex-col items-center justify-center p-4">
          <div className="mb-2 text-yellow-400">
            <Image className="w-8 h-8" />
          </div>
          <p className="mb-4 text-center text-gray-300">
            Pinterest content cannot be embedded directly
          </p>
          <a
            href={media.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 transition-colors bg-red-600 rounded-lg hover:bg-red-700"
          >
            <ExternalLink className="w-4 h-4" />
            View on Pinterest
          </a>
        </div>
      );
    }

    // Regular image
    return (
      <div className="flex justify-center">
        <img
          src={media.url}
          alt={project.title}
          className="w-auto max-h-[75vh] rounded-lg"
          onError={(e) => {
            console.error("Error loading media:", media.url);
            e.currentTarget.src = "/placeholder-image.jpg";
          }}
        />
      </div>
    );
  };

  if (!project) {
    return (
      <div className="flex items-center justify-center min-h-screen text-white bg-gray-900">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold">Project Not Found</h1>
          <Link to="/projects" className="text-blue-400 hover:text-blue-300">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <nav className="fixed top-0 z-50 w-full border-b bg-slate-900/80 backdrop-blur-sm border-slate-700">
        <div className="flex items-center justify-between max-w-6xl px-6 py-4 mx-auto">
          <Link
            to="/projects"
            className="flex items-center gap-2 text-blue-400 hover:text-blue-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Projects
          </Link>
          <h1 className="text-2xl font-bold">{project.title}</h1>
        </div>
      </nav>

      <div className="max-w-6xl p-4 pt-32 mx-auto">
        {/* Media Gallery */}
        <div className="relative mb-8">
          <div className="rounded-lg overflow-hidden bg-transparent min-h-[400px] flex items-center justify-center">
            {project.media && project.media.length > 0 ? (
              renderMedia(project.media[currentMediaIndex])
            ) : project.images && project.images.length > 0 ? (
              <img
                src={project.images[currentMediaIndex]}
                alt={project.title}
                className="w-auto max-h-[75vh] rounded-lg"
              />
            ) : (
              <div className="flex items-center justify-center w-full h-64 text-gray-400 bg-gray-800 rounded-lg">
                No media available
              </div>
            )}
          </div>

          {(project.media?.length > 1 || project.images?.length > 1) && (
            <>
              <button
                onClick={prevMedia}
                className="absolute flex items-center justify-center w-10 h-10 transition-all transform -translate-y-1/2 rounded-full left-4 top-1/2 bg-black/70 hover:bg-black/90"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextMedia}
                className="absolute flex items-center justify-center w-10 h-10 transition-all transform -translate-y-1/2 rounded-full right-4 top-1/2 bg-black/70 hover:bg-black/90"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Media Indicators */}
              <div className="absolute flex gap-2 transform -translate-x-1/2 bottom-4 left-1/2">
                {(project.media || project.images).map((media, index) => {
                  const mediaUrl =
                    typeof media === "string" ? media : media.url;
                  const mediaType =
                    typeof media === "string"
                      ? "image"
                      : media.type || getMediaType(mediaUrl);

                  return (
                    <button
                      key={index}
                      onClick={() => setCurrentMediaIndex(index)}
                      className={`flex items-center justify-center w-8 h-8 rounded-full transition-all ${
                        index === currentMediaIndex
                          ? "bg-white text-black"
                          : "bg-white/50 text-white hover:bg-white/70"
                      }`}
                      title={
                        mediaType === "video"
                          ? "Video"
                          : mediaType === "pinterest"
                          ? "Pinterest"
                          : "Image"
                      }
                    >
                      {mediaType === "video" ? (
                        <Play className="w-3 h-3" fill="currentColor" />
                      ) : mediaType === "pinterest" ? (
                        <span className="text-[10px] font-bold">P</span>
                      ) : (
                        <Image className="w-3 h-3" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Media Counter */}
              <div className="absolute flex items-center gap-2 px-3 py-1 text-sm rounded-full top-4 right-4 bg-black/70">
                {currentMediaIndex + 1} /{" "}
                {(project.media || project.images).length}
              </div>
            </>
          )}
        </div>

        {/* Rest of the content */}
        <div className="grid gap-8 md:grid-cols-3">
          <div className="space-y-6 md:col-span-2">
            <div>
              <h2 className="mb-2 text-xl font-bold">Overview</h2>
              <p className="text-gray-300">{project.overview}</p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="mb-2 text-lg font-bold text-blue-400">
                  Challenge
                </h3>
                <u className="space-y-2 text-gray-300 no-underline">
                  {project.challenge.map((challenge, index) => (
                    <li key={index} className="flex items-start">
                      <span className="mr-3 text-blue-400">•</span>
                      <span>{challenge}</span>
                    </li>
                  ))}
                </u>
              </div>
              <div>
                <h3 className="mb-2 text-lg font-bold text-blue-400">
                  Solution
                </h3>
                <u className="space-y-2 text-gray-300 no-underline">
                  {project.solution.map((solution, index) => (
                    <li key={index} className="flex items-start">
                      <span className="mr-3 text-blue-400">•</span>
                      <span>{solution}</span>
                    </li>
                  ))}
                </u>
              </div>
            </div>

            <div>
              <h3 className="mb-4 text-lg font-bold text-blue-400">Results</h3>
              <div className="space-y-2">
                {project.results.map((result, index) => (
                  <div key={index} className="p-3 bg-gray-800 rounded">
                    <p className="text-gray-300">{result}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="p-4 bg-gray-800 rounded-lg">
              <h3 className="mb-3 font-bold text-blue-400">Project Info</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-400">Role:</span> {project.role}
                </div>
                <div>
                  <span className="text-gray-400">Year:</span> {project.year}
                </div>
                <div>
                  <span className="text-gray-400">Duration:</span>{" "}
                  {project.duration}
                </div>
                <div>
                  <span className="text-gray-400">Category:</span>{" "}
                  {project.category}
                </div>
              </div>
            </div>

            <div className="p-4 bg-gray-800 rounded-lg">
              <h3 className="mb-3 font-bold text-blue-400">Technologies</h3>
              <div className="flex flex-wrap gap-2">
                {project.tools.map((tool, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 text-sm bg-gray-700 rounded"
                  >
                    {tool}
                  </span>
                ))}
              </div>
            </div>

            <div className="p-4 bg-gray-800 rounded-lg">
              <h3 className="mb-3 font-bold text-blue-400">Links</h3>
              <div className="space-y-2">
                {project.liveUrl && (
                  <a
                    href={project.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 text-sm transition-colors bg-blue-600 rounded hover:bg-blue-700"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Live Demo
                  </a>
                )}
                {project.githubUrl && (
                  <a
                    href={project.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 text-sm transition-colors bg-gray-700 rounded hover:bg-gray-600"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Source Code
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
